import React, { useState, useEffect } from 'react';
import { Music, Play, Pause, Volume2, Sparkles, Activity, UserCheck, Disc, Waves, ShieldCheck } from 'lucide-react';
import { audioEngine } from '../../utils/audioEngine';
import { DEMO_STUDENTS } from '../../data/demoStudents';

export const MusicTherapyModule: React.FC = () => {
  const [selectedStudentId, setSelectedStudentId] = useState(DEMO_STUDENTS[2].id); // Master Aarav Patel
  const [bpm, setBpm] = useState(72);
  const [isPlayingMetronome, setIsPlayingMetronome] = useState(false);
  const [beatPulse, setBeatPulse] = useState(false);
  const [activeRaga, setActiveRaga] = useState<'bhupali' | 'yaman' | 'bairagi'>('bhupali');
  const [isBinauralPlaying, setIsBinauralPlaying] = useState(false);

  const activeStudent = DEMO_STUDENTS.find(s => s.id === selectedStudentId) || DEMO_STUDENTS[2];

  const pentatonicNotes = [
    { label: 'C4', freq: 261.63, noteName: 'Do / Sa' },
    { label: 'D4', freq: 293.66, noteName: 'Re' },
    { label: 'E4', freq: 329.63, noteName: 'Mi / Ga' },
    { label: 'G4', freq: 392.00, noteName: 'Sol / Pa' },
    { label: 'A4', freq: 440.00, noteName: 'La / Dha' },
    { label: 'C5', freq: 523.25, noteName: 'High Sa' },
    { label: 'D5', freq: 587.33, noteName: 'High Re' },
    { label: 'E5', freq: 659.25, noteName: 'High Ga' }
  ];

  useEffect(() => {
    return () => {
      audioEngine.stopMetronome();
      audioEngine.stopAmbientSoundscape();
    };
  }, []);

  const handleToggleMetronome = () => {
    if (isPlayingMetronome) {
      audioEngine.stopMetronome();
      setIsPlayingMetronome(false);
    } else {
      setIsPlayingMetronome(true);
      audioEngine.startMetronome(bpm, () => {
        setBeatPulse(prev => !prev);
      });
    }
  };

  const handlePlayRaga = (raga: 'bhupali' | 'yaman' | 'bairagi') => {
    setActiveRaga(raga);
    audioEngine.playRagaSequence(raga);
  };

  const handleToggleBinaural = () => {
    if (isBinauralPlaying) {
      audioEngine.stopAmbientSoundscape();
      setIsBinauralPlaying(false);
    } else {
      setIsBinauralPlaying(true);
      audioEngine.startAmbientSoundscape('alpha_focus');
    }
  };

  const handleBpmChange = (newBpm: number) => {
    const clamped = Math.max(40, Math.min(160, newBpm));
    setBpm(clamped);
    if (isPlayingMetronome) {
      audioEngine.startMetronome(clamped, () => {
        setBeatPulse(prev => !prev);
      });
    }
  };

  const handlePlayKey = (freq: number) => {
    audioEngine.playNote(freq, 'sine', 0.9);
  };

  return (
    <div className="p-6 space-y-6 bg-slate-900 text-white rounded-2xl border border-slate-800">
      
      {/* Demo Student Header */}
      <div className="bg-slate-950 border border-slate-800 p-3 rounded-xl flex flex-col md:flex-row items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-violet-400 shrink-0" />
          <span className="font-bold text-slate-300">Active Patient:</span>
          <select 
            value={selectedStudentId}
            onChange={(e) => setSelectedStudentId(e.target.value)}
            className="px-2.5 py-1 bg-slate-900 border border-violet-500/40 rounded-lg font-bold text-violet-300 focus:ring-2 focus:ring-violet-500"
          >
            {DEMO_STUDENTS.map(st => (
              <option key={st.id} value={st.id}>{st.name} ({st.rollId}) - {st.disabilityCategory}</option>
            ))}
          </select>
        </div>

        <div className="text-[11px] text-violet-300 font-semibold">
          Gait Target: <span className="font-bold">{activeStudent.iepGoal}</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-500/20 text-violet-300 text-xs font-bold rounded-full border border-violet-500/30">
            <Music className="w-3.5 h-3.5" /> Module 22: Music & Rhythmic Auditory Stimulation (RAS)
          </div>
          <h2 className="text-2xl font-black mt-2">Indian Classical Raga Therapy & RAS Gait Entrainment</h2>
        </div>
        <div className="text-right">
          <div className="text-3xl font-black text-violet-400 font-mono">{bpm} BPM</div>
          <div className="text-xs text-slate-400">Auditory Metronome Tempo</div>
        </div>
      </div>

      {/* Indian Classical Raga Therapy Controls */}
      <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold text-amber-300 uppercase tracking-wider flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" /> Indian Classical Raga Therapy Scales (RPwD / NEP 2020 Guidelines)
          </h3>
          <span className="text-[10px] text-slate-400">Auditory sensory soothing for neurodivergent students</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button
            onClick={() => handlePlayRaga('bhupali')}
            className={`p-4 rounded-xl border text-left transition ${
              activeRaga === 'bhupali' ? 'bg-amber-500/20 border-amber-400 text-amber-200' : 'bg-slate-900 border-slate-800 hover:border-slate-700'
            }`}
          >
            <div className="font-bold text-xs text-amber-300">🧘 Raga Bhupali (Pentatonic)</div>
            <div className="text-[11px] text-slate-400 mt-1">Calming, grounding, anxiety reduction for ASD.</div>
          </button>

          <button
            onClick={() => handlePlayRaga('yaman')}
            className={`p-4 rounded-xl border text-left transition ${
              activeRaga === 'yaman' ? 'bg-amber-500/20 border-amber-400 text-amber-200' : 'bg-slate-900 border-slate-800 hover:border-slate-700'
            }`}
          >
            <div className="font-bold text-xs text-amber-300">🌅 Raga Yaman (Peaceful)</div>
            <div className="text-[11px] text-slate-400 mt-1">Evening relaxation & emotional regulation.</div>
          </button>

          <button
            onClick={() => handlePlayRaga('bairagi')}
            className={`p-4 rounded-xl border text-left transition ${
              activeRaga === 'bairagi' ? 'bg-amber-500/20 border-amber-400 text-amber-200' : 'bg-slate-900 border-slate-800 hover:border-slate-700'
            }`}
          >
            <div className="font-bold text-xs text-amber-300">🎵 Raga Bairagi (Serenity)</div>
            <div className="text-[11px] text-slate-400 mt-1">Auditory overstimulation meltdown recovery.</div>
          </button>
        </div>
      </div>

      {/* Binaural Brainwave Entrainment Card */}
      <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 flex items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="text-xs font-bold text-emerald-300 flex items-center gap-2">
            <Waves className="w-4 h-4 text-emerald-400" />
            10Hz Alpha Binaural Focus Brainwave Entrainment
          </div>
          <div className="text-xs text-slate-400">
            Synthesizes 200Hz / 210Hz stereo beats for classroom attention & learning readiness.
          </div>
        </div>

        <button
          onClick={handleToggleBinaural}
          className={`px-4 py-2.5 rounded-xl font-bold text-xs flex items-center gap-2 shadow transition ${
            isBinauralPlaying ? 'bg-amber-500 text-slate-950' : 'bg-emerald-600 text-white hover:bg-emerald-500'
          }`}
        >
          {isBinauralPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-current" />}
          {isBinauralPlaying ? 'Stop Alpha Wave' : 'Play 10Hz Alpha Focus'}
        </button>
      </div>

      {/* Metronome Control Panel */}
      <div className="bg-slate-950 p-6 rounded-2xl border border-slate-800 space-y-4 text-center relative overflow-hidden">
        <div className="flex items-center justify-center gap-3">
          <div className={`w-6 h-6 rounded-full transition-all duration-150 ${beatPulse ? 'bg-violet-400 scale-125 shadow-lg shadow-violet-500/50' : 'bg-slate-800'}`}></div>
          <div className="text-4xl font-black font-mono text-violet-400">{bpm} Beats / Min</div>
        </div>

        <div className="flex items-center justify-center gap-4">
          <button 
            onClick={() => handleBpmChange(bpm - 4)}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-xs font-bold transition"
          >
            - 4 BPM
          </button>

          <button 
            onClick={handleToggleMetronome}
            className={`px-6 py-3 rounded-xl font-bold text-xs flex items-center gap-2 shadow-lg transition transform active:scale-95 ${
              isPlayingMetronome ? 'bg-amber-500 text-slate-950 hover:bg-amber-400' : 'bg-violet-600 text-white hover:bg-violet-500'
            }`}
          >
            {isPlayingMetronome ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-current" />}
            {isPlayingMetronome ? 'Pause Auditory Metronome' : 'Start Web Audio RAS Metronome'}
          </button>

          <button 
            onClick={() => handleBpmChange(bpm + 4)}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-xs font-bold transition"
          >
            + 4 BPM
          </button>
        </div>
      </div>

      {/* Interactive Pentatonic Musical Keyboard */}
      <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold text-violet-300 uppercase tracking-wider flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400" /> Interactive Pentatonic Tone Keyboard (Web Audio Synthesizer)
          </h3>
          <span className="text-[10px] text-slate-400">Click any key to play pitch-matched tones</span>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
          {pentatonicNotes.map((note) => (
            <button
              key={note.label}
              onClick={() => handlePlayKey(note.freq)}
              className="py-6 px-2 bg-gradient-to-b from-slate-900 to-slate-800 hover:from-violet-900 hover:to-indigo-900 border border-slate-700 hover:border-violet-400 rounded-xl flex flex-col items-center justify-between text-center transition transform active:scale-95 shadow-md group"
            >
              <span className="text-lg font-black text-violet-300 group-hover:text-white font-mono">{note.label}</span>
              <span className="text-[10px] text-slate-400 group-hover:text-amber-300 font-bold">{note.noteName}</span>
            </button>
          ))}
        </div>
      </div>

    </div>
  );
};
